import {axiosInstance, Data, OResult} from "./axios-instance";

export interface User {
    ID: number,
    LOGIN: string,
    EMAIL?: string,
    TEL?: string,
    CREATED: number,
    VERSION: number,
    TFA_LOGIN_CHECK?: boolean,
    TFA_TYPE?: number,
    TFA_PARAM?: string,
    TFA_DURATION?: number,
}

export interface UserLoginResult {
    userId: number,
    permissions: string[],
    roles: string[],
    isExecutor: boolean,
    isOperator: boolean,
    isEmployer: boolean,
}

export interface TfaState {
    status: boolean,
    id: number,
    timeout: number,
}

export interface Profile {
    USER_ID: number,
    NICK_NAME: string,
    FIRST_NAME: string,
    SECOND_NAME: string,
    LAST_NAME: string,
    DESCRIPTION: string,
    TEL: string,
    COUNTRY: string,
    IMAGE: string,
    SEX: number,
    DATE_OF_BIRTH: number,
    PASS_NUMBER: string,
    PASS_DATE: number,
    PASS_DEPARTMENT_CODE: string,
    PASS_DEPARTMENT: string,
    PASS_PLACE_OF_BIRTH: string,
    PASS_ADDRESS: string,
    CODE_INN: string,
    CODE_KPP: string,
    CODE_SNILS: string,
    VERSION: number,
}

export const userApi = {
    isAuth: async () => {
        const response = await axiosInstance.get<Data<OResult>[]>(
            `/sec/isauth`
        );
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    login: async (username: string, password: string) => {
        const params = new URLSearchParams();
        params.append("login", username);
        params.append("password", password);
        params.append("rememberMe", "true");
        const response = await axiosInstance.post<Data<UserLoginResult>[]>(`/sec/login`, params);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        return response.data[0].data[0];
    },
    logout: async () => {
        // axiosInstance.defaults.headers = {};
        const response = await axiosInstance.get<Data<OResult>[]>(
            `/sec/logout`
        );
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    reg: async (login: string, password: string, email: string, nickName: string, firstName: string, secondName: string, lastName: string, description: string, tel: string,
                country: string, sex: number, dateOfBirth: number, passNumber: string, passDate: number, passDepartmentCode: string, passDepartment: string,
                passPlaceOfBirth: string, passAddress: string, codeInn: string, codeKpp: string, codeSnils: string) => {
        const params = new URLSearchParams();
        params.append("login", login);
        params.append("password", password);
        params.append("email", email);
        params.append("nickName", nickName);
        params.append("firstName", firstName);
        params.append("secondName", secondName);
        params.append("lastName", lastName);
        params.append("description", description);
        params.append("tel", tel);
        params.append("country", country);
        params.append("sex", String(sex));
        params.append("dateOfBirth", String(dateOfBirth));
        params.append("passNumber", passNumber);
        params.append("passDate", String(passDate));
        params.append("passDepartmentCode", passDepartmentCode);
        params.append("passDepartment", passDepartment);
        params.append("passPlaceOfBirth", passPlaceOfBirth);
        params.append("passAddress", passAddress);
        params.append("codeInn", codeInn);
        if (codeKpp && codeKpp.trim())
            params.append("codeKpp", codeKpp);
        if (codeSnils && codeSnils.trim())
            params.append("codeSnils", codeSnils);
        const response = await axiosInstance.post<Data<OResult>[]>(`/sec/user/reg`, params);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    changePassword: async (password: string) => {
        const params = new URLSearchParams();
        params.append("password", password);
        const response = await axiosInstance.post<Data<OResult>[]>(`/sec/user/change/password`, params);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    userFind: async (id: number) => {
        const response = await axiosInstance.get<Data<User>[]>(
            `/sec/user/${id}`
        );
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        return response.data[0].data[0];
    },
    userOwnGet: async () => {
        const response = await axiosInstance.get<Data<User>[]>(`/sec/user/own`);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        return response.data[0].data[0];
    },
    tfaState: async () => {
        const response = await axiosInstance.get<Data<TfaState>[]>(`/sec/tfa/state`);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        return response.data[0].data[0];
    },
    tfaCheck: async (phrase: string) => {
        const params = new URLSearchParams();
        params.append("phrase", phrase);
        const response = await axiosInstance.post<Data<OResult>[]>(`/sec/tfa/check`, params);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    tfaCancel: async () => {
        const response = await axiosInstance.get<Data<OResult>[]>(`/sec/tfa/cancel`);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    changeEmail: async (email: string) => {
        const params = new URLSearchParams();
        params.append("email", email);
        const response = await axiosInstance.post<Data<OResult>[]>(`/sec/user/change/email`, params);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    changeTel: async (tel: string) => {
        const params = new URLSearchParams();
        params.append("tel", tel);
        const response = await axiosInstance.post<Data<OResult>[]>(`/sec/user/change/tel`, params);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    resetPassword: async (email: string, tel: string, password: string) => {
        const params = new URLSearchParams();
        params.append("email", email);
        params.append("tel", tel);
        params.append("password", password);
        const response = await axiosInstance.post<Data<OResult>[]>(`/sec/user/reset/password`, params);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    isLoginExist: async (login: string) => {
        const params = new URLSearchParams();
        params.append("login", login);
        const response = await axiosInstance.get<Data<OResult>[]>(`/sec/user/isloginexist`, {params: params});
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    isEmailExist: async (email: string) => {
        const params = new URLSearchParams();
        params.append("email", email);
        const response = await axiosInstance.get<Data<OResult>[]>(`/sec/user/isemailexist`, {params: params});
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    isTelExist: async (tel: string) => {
        const params = new URLSearchParams();
        params.append("tel", tel);
        const response = await axiosInstance.get<Data<OResult>[]>(`/sec/user/istelexist`, {params: params});
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    profileGet: async (id: number) => {
        const params = new URLSearchParams();
        params.append("id", String(id));
        const response = await axiosInstance.get<Data<Profile>[]>(`/profile/get`, {params: params});
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        return response.data[0].data[0];
    },
    profileList: async (filter: string, offset: number, limit: number) => {
        const params = new URLSearchParams();
        params.append("filter", filter);
        params.append("offset", String(offset));
        params.append("limit", String(limit));
        const response = await axiosInstance.get<Data<Profile>[]>(`/profile/get`, {params: params});
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data;
        return resultData;
    },
    profileListCount: async (filter: string) => {
        const params = new URLSearchParams();
        params.append("filter", filter);
        const response = await axiosInstance.get<Data<OResult>[]>(`/profile/count`, {params: params});
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return resultData && resultData.result ? resultData.result as number : 0;
    },
    profileOwnEdit: async (nickName: string, firstName: string, secondName: string, lastName: string, description: string, tel: string, country: string, image: string,
                           sex: number, dateOfBirth: number, passNumber: string, passDate: number, passDepartmentCode: string, passDepartment: string,
                           passPlaceOfBirth: string, passAddress: string, codeInn: string, codeKpp: string, codeSnils: string) => {
        const params = new URLSearchParams();
        params.append("nickName", nickName);
        params.append("firstName", firstName);
        params.append("secondName", secondName);
        params.append("lastName", lastName);
        params.append("description", description);
        params.append("tel", tel);
        params.append("country", country);
        params.append("image", image);
        params.append("sex", String(sex));
        params.append("dateOfBirth", String(dateOfBirth));
        params.append("passNumber", passNumber);
        params.append("passDate", String(passDate));
        params.append("passDepartmentCode", passDepartmentCode);
        params.append("passDepartment", passDepartment);
        params.append("passPlaceOfBirth", passPlaceOfBirth);
        params.append("passAddress", passAddress);
        params.append("codeInn", codeInn);
        if (codeKpp && codeKpp.trim())
            params.append("codeKpp", codeKpp);
        if (codeSnils && codeSnils.trim())
            params.append("codeSnils", codeSnils);
        const response = await axiosInstance.post<Data<OResult>[]>(`/profile/edit`, params);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        const resultData = response.data[0].data[0];
        return !!resultData.result;
    },
    profileOwnGet: async () => {
        const response = await axiosInstance.get<Data<Profile>[]>(
            `/profile/own`
        );
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        return response.data[0].data[0];
    },
    // profileGetByInn: async (codeInn: string) => {
    //     const params = new URLSearchParams();
    //     params.append("codeInn", codeInn);
    //     const response = await axiosInstance.get<Data<Profile>[]>(`/profile/getbyinn`, {params: params});
    //     if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
    //         throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
    //     return response.data[0].data[0];
    // },
    getOwnData: async () => {
        const response = await axiosInstance.get<Data<UserLoginResult>[]>(`/sec/owndata`);
        if (!Array.isArray(response.data) || response.data[0].errorCode || !Array.isArray(response.data[0].data))
            throw new Error(response.data && response.data[0] && response.data[0].errorText ? response.data[0].errorText : "");
        return response.data[0].data[0];
    },

};
